import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { IProductionRecurringTime, Log } from "@ryde/shared";

import { ColorIconButton } from "../../../controls";
import { TimeTemplate } from "../time.template";
import {
  emptyTimeItem,
  ITimeListItemForm,
  timeItemFormFieldNames,
} from "./time-list-item.models";
import { useTimeListItemResolver } from "./use-time-list-item-validation";

export const TimeListItemEdit: FC<ITimeListItemForm> = ({
  value,
  onCancelOuter,
  setValueOuter,
  fieldName,
}) => {
  const { t } = useTranslation();
  const { timeItemFormValidationSchema } = useTimeListItemResolver();

  const methods = useForm<Partial<IProductionRecurringTime>>({
    defaultValues: value || emptyTimeItem,
    mode: "all",
    reValidateMode: "onChange",
    resolver: yupResolver(timeItemFormValidationSchema),
  });

  const {
    trigger,
    getValues,
    watch,
    formState: { isValid, touchedFields, dirtyFields },
  } = methods;

  const onSaveClick = useEvent(async (): Promise<void> => {
    const validated = await trigger();

    if (!validated) {
      return;
    }

    const valueSnapshot = getValues();

    Log.debug(
      `Set value for recurring production time item target field: ${fieldName}`,
      valueSnapshot,
    );
    setValueOuter?.(fieldName, valueSnapshot, {
      shouldValidate: true,
      shouldDirty: true,
    });
    onCancelOuter?.();
  });

  const fieldTimeValue = watch("time");

  useEffect(() => {
    for (const dependantFieldName of timeItemFormFieldNames) {
      Log.debug(
        "Trigger dependant controls validation for ",
        dependantFieldName,
        fieldTimeValue,
      );

      if (
        touchedFields[dependantFieldName] ||
        dirtyFields[dependantFieldName]
      ) {
        trigger(dependantFieldName);
      }
    }
  }, [trigger, fieldTimeValue, touchedFields, dirtyFields]);

  return (
    <FormProvider {...methods}>
      <div className="time-list-item">
        <div className="time-list-item__editors">
          <TimeTemplate
            name="time"
            labelTranslated={t("produceButton.formLabel.recurring.produceAt")}
          />
        </div>
        <div className="time-list-item__buttons">
          <ColorIconButton
            disabled={!isValid}
            iconType="CHECK"
            edge="end"
            aria-label="save"
            onClick={onSaveClick}
          />

          <ColorIconButton
            iconType="CANCEL"
            edge="end"
            aria-label="cancel"
            onClick={() => onCancelOuter?.()}
          />
        </div>
      </div>
    </FormProvider>
  );
};
